import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const typewriterWithCaretSkill: Skill = {
    name: SKILL_NAMES.typewriterWithCaret,
    kind: 'codeo',
    description:
        'Type a line out character by character behind a blinking caret — for a prompt, a search ' +
        'query, a message being written, or anything the viewer should feel is being composed live.',
    instructions: `# Typewriter With Caret

Characters appear one at a time with a caret riding the end of the line. It reads as composition in progress — someone is writing this *now* — which is why it suits a prompt, a query, or a message, and why it is wrong for a headline (a headline is a statement, not an act).

Both the typing and the caret blink come off the timeline. A CSS-animated caret would freeze at its first frame when the composition is rasterized.

## Markup

The caret is a sibling of the text, not a border on it, so it can blink independently and sit at the correct height.

\`\`\`html
<div id="prompt-line" style="position: absolute; left: 240px; top: 480px; display: flex; align-items: center; gap: 6px; font-family: var(--l5-font-0), monospace; font-size: 64px; font-weight: 600; color: var(--l5-color-0, #101820);">
  <span id="prompt-text" style="white-space: pre;"></span>
  <span id="prompt-caret" style="width: 4px; height: 68px; background: var(--l5-color-1, #38bdf8);"></span>
</div>
\`\`\`

## Timeline code

Type by tweening a character count and slicing — not by pushing characters on an interval, which has no defined value when the renderer seeks to an arbitrary progress.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const LINE = 'summarize this quarter in one slide';
const textEl = document.querySelector('#prompt-text');
const typing = { count: 0 };

tl.to(typing, {
    count: LINE.length,
    duration: 1.8,
    ease: 'none',
    onUpdate: () => {
        textEl.textContent = LINE.slice(0, Math.floor(typing.count));
    },
}, 0.25);

// A finite blink: 12 half-cycles of a hard on/off. steps(1) makes it snap rather than fade.
tl.to('#prompt-caret', {
    opacity: 0,
    duration: 0.45,
    ease: 'steps(1)',
    repeat: 11,
    yoyo: true,
}, 0);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Rhythm**: \`ease: 'none'\` types at a constant rate. \`'power1.out'\` starts fast and slows, which reads as someone trailing off; \`'power1.in'\` reads as them warming up.
- **Speed**: roughly 18–24 characters per second is legible and still feels live. Faster reads as a paste.
- **Word-at-a-time**: tween a word index instead of a character count and slice on a pre-split array — better for a long line where per-character typing would take too long.
- **Caret shapes**: a block caret (\`width: 28px\`, translucent) reads as a terminal; a thin rule reads as a text field.
- **Caret drop**: stop the blink and fade the caret out once typing ends, so the line settles into a finished statement.
- **A response beneath**: after the line completes, a second block can fade in under it — that is a phase change, and the caret's disappearance is what hands off.

## Pitfalls

- Never build the blink with CSS \`animation\` or a \`setInterval\`; both run on wall-clock time and freeze when the composition is rasterized off-screen. A finite \`repeat\` with \`yoyo\` on the timeline is the only version that survives seeking.
- Never use \`repeat: -1\` for the blink. The host maps a normalized progress across the scene and needs a finite duration.
- Reserve the line's height (\`white-space: pre\` and a fixed caret height) or the flex row jumps as the first character lands.
- Don't type a headline. Typing is an act; a headline should already be true when the scene starts.
- Keep it to one line. Typing a paragraph out is dead air in a video, however faithful it is.
- If the scene has narration, the line should finish typing at or just before the word that names it — a query still typing while the voiceover has moved on reads as lag.
- The typed element's \`textContent\` is rewritten every frame, so don't nest anything inside \`#prompt-text\`.`,
};
